import { useEffect, useState } from "react";
import { GlobalTable } from "../../../global/components/GlobalTable";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { Button, Col, Form, Input, Row, Select } from "antd";
import ConfirmModal from "../../../global/components/ConfirmModal";
import useDelete from "../../../global/hooks/useDelete";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";
import CommonBreadcrumb from "../../../global/components/CommonBreadcrumb";
import { getModifiedResponse } from "../../../global/utils/responseModifier";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";
import { HiArrowPath } from "react-icons/hi2";
import useGet from "../../../global/hooks/useGet";
import { PurchaseColumns } from "../Components/Columns";
import {
  getPurchaseOpenPath,
  PURCHASE_LIST_SEARCH_KEY,
} from "../utils/listSearchStorage";
import {
  getDealerNo,
  getOemNo,
  hasRole,
} from "../../../global/hooks/getUserRole";
import CustomDateTimePicker from "../../../global/components/inputWidgets/CustomDatePicker";
import { purchaseTypeOptions } from "../../../utils/Common";
import dayjs from "dayjs";
import { convertToUnix } from "../../../global/utils/unixToLocalTime";

const initialFilterState = {
  PurchaseNo: "",
  OemNo: "",
  PurchaseName: "",
  PurchaseTime: "",
  Comment: "",
};

export const Purchase = () => {
  const [form] = Form.useForm();
  const [hoveredRow, setHoveredRow] = useState(null);
  const [listData, setListData] = useState([]);
  const [params, setParams] = useSearchParams({
    limit: 10,
    sort_by: "PurchaseNo",
    order: "desc",
  });
  const logData = JSON.parse(sessionStorage.getItem("userData"));
  /** Dealer accounts (roles 7, 8): scoped to own dealer; OEM/Dealer filters hidden, DealerNo sent on API only. */
  const isDealerAccount = hasRole(7, 8);
  /** OEM accounts (roles 4–6): scoped to own OEM; OEM filter/column hidden, OemNo sent on API only. */
  const isOemAccount = hasRole(4, 5, 6);
  const canManagePurchase = hasRole(1, 2, 7, 8);
  const modParams = { ...Object.fromEntries([...params?.entries()]) } || {}; //
  const initialPagination = {
    current: parseInt(modParams.page) || 1,
    pageSize: parseInt(modParams.limit) || 10,
    total: 0,
  };
  const [pagination, setPagination] = useState(initialPagination);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [filter, setFilter] = useState(initialFilterState);
  const [showModal, setShowModal] = useState(false);
  const handleFormChange = (changedValues) => {
    if ("PurchaseTime" in changedValues) {
      setFilter((prev) => ({
        ...prev,
        PurchaseTime: changedValues.PurchaseTime,
      }));
    }
  };

  useEffect(() => {
    if (modParams?.PurchaseTime) {
      form.setFieldsValue({
        PurchaseTime: dayjs.unix(Number(modParams.PurchaseTime)),
      });
    }
  }, [params]);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    sessionStorage.setItem(PURCHASE_LIST_SEARCH_KEY, location.search || "");
  }, [location.search]);

  // to get table data from API server (DealerNo / OemNo for dealer / OEM login are added here only, not in the URL)
  const query = new URLSearchParams(params);
  query.delete("pageSize");
  query.delete("total");
  query.delete("current");
  if (isDealerAccount) {
    const dn = getDealerNo();
    if (dn != null && dn !== "") {
      query.set("DealerNo", String(dn));
    }
  }
  if (isOemAccount) {
    const on = getOemNo();
    if (on != null && on !== "") {
      query.set("OemNo", String(on));
    }
  }
  const updatedQuery = query.toString();
  const url = updatedQuery
    ? `${process.env.REACT_APP_API_URL}/api/purchases/?${updatedQuery}`
    : `${process.env.REACT_APP_API_URL}/api/purchases/`;
  const { data, loading, error, refetch } = useGet(url);
  const optionUrl = `${process.env.REACT_APP_API_URL}/api/accounts/options/`;
  const dealerOptionsOemNo = isOemAccount ? getOemNo() : filter?.OemNo;
  const dealerOptionsUrl =
    !isDealerAccount && dealerOptionsOemNo
      ? `${process.env.REACT_APP_API_URL}/api/dealers/oems/${dealerOptionsOemNo}/dealers/`
      : null;

  const {
    data: optionData,
    loading: optionDataLoading,
    error: optionDataError,
  } = useGet(optionUrl);
  const { data: dealerOptionsByOem } = useGet(dealerOptionsUrl);
  const modifiedOems = getModifiedResponse(optionData, "oems");
  const modifiedDealers = getModifiedResponse(dealerOptionsByOem, null);

  let deleteUrl;
  selectedRowKeys.length > 1
    ? (deleteUrl = `${process.env.REACT_APP_API_URL}/api/purchases/bulk-delete/`)
    : (deleteUrl = `${process.env.REACT_APP_API_URL}/api/purchases/${selectedRowKeys}/`);

  const {
    data: deletedData,
    loading: deletedDataLoading,
    error: deletedDataError,
    deleteData,
    deleteMultiple,
  } = useDelete(deleteUrl);

  useEffect(() => {
    const matchedFilter = {};
    for (const key in initialFilterState) {
      if (modParams[key] !== undefined) {
        matchedFilter[key] = modParams[key];
      }
    }

    setFilter((prev) => ({
      ...prev,
      ...matchedFilter,
    }));
  }, []); //

  const paginationConfig = {
    current: pagination.current, //current page
    pageSize: pagination.pageSize, // items per page
    total: data?.count, // total items
    showSizeChanger: true,
    pageSizeOptions: [10, 20, 50, 100],
    showTotal: (total, range) => `Total ${total} items ${range[0]}-${range[1]}`,
    onChange: (page, pageSize) => {
      setPagination({
        ...pagination,
        current: page,
        pageSize: pageSize,
      });

      const updatedParams = {
        ...Object.fromEntries([...params.entries()]),
        limit: pageSize,
        page: page,
      };
      setParams(updatedParams);
    },
  };

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    fixed: true,
  };

  const onRow = (record, rowIndex) => {
    return {
      onClick: (event) => {
        navigate(getPurchaseOpenPath(record, canManagePurchase));
      },
    };
  };

  const handleFilterChange = (e) => {
    const inputName = e.target.name;
    const inputValue = e.target.value.replace(/['""`''""]/g, "");
    setFilter({ ...filter, [inputName]: inputValue });
  };

  const handleFilter = (e) => {
    const filterDict = {};
    for (const key in filter) {
      if (
        filter[key] !== null &&
        filter[key] !== "" &&
        filter[key] !== undefined
      ) {
        if (key === "PurchaseTime") {
          filterDict[key] = convertToUnix(filter[key]);
        } else {
          filterDict[key] = filter[key];
        }
      }
    }
    const isEmpty = JSON.stringify(filterDict) === "{}";

    if (isEmpty) {
      handleReset();
    } else {
      const params = {
        limit: 10,
        sort_by: "PurchaseNo",
        order: "desc",
        page: 1,
        ...filterDict,
      };
      setPagination((prev) => ({ ...prev, current: 1 }));
      setParams(params);
      refetch();
    }
  };

  // reset
  const handleReset = (e) => {
    // Reset the filter state to the initial state
    setFilter(initialFilterState);
    setPagination({
      current: 1,
      pageSize: 10,
      total: 0,
    }); //
    // Define the new parameters for the API call
    const params = {
      limit: 10,
      sort_by: "PurchaseNo", // Make sure to specify sorting if necessary
      order: "desc",
    };
    setParams(params);
    // setSelectedRowKeys([]);
    refetch();
  };

  const onDeleteConfirm = async () => {
    try {
      if (selectedRowKeys.length === 1) {
        await deleteData();
      } else {
        await deleteMultiple({ ids: selectedRowKeys });
      }
    } catch (error) {
      openErrorNotification("Something went wrong.");
    }
    setShowModal(false);
    setSelectedRowKeys([]);
  };

  useEffect(() => {
    if (deletedData && !deletedDataError) {
      openConfirmNotification("Success");

      refetch();
    } else if (deletedDataError) {
      openErrorNotification(
        deletedDataError?.response?.data?.message || "Something went wrong",
      );
    }
  }, [deletedData, deletedDataError]);

  useEffect(() => {
    if (data && !loading) {
      const newData = data?.data?.map((el) => ({
        ...el,
        key: el?.PurchaseNo,
      }));
      setListData(newData);
    }
    // setSelectedRowKeys([]);
  }, [data, loading]);

  const breadcrumbItems = [
    {
      label: "Purchase",
      href: "/purchase",
    },
  ];

  return (
    <div>
      <div className="pb-4 -mt-4 pt-[6px]">
        <CommonBreadcrumb breadcrumbItems={breadcrumbItems} />
      </div>
      <div className="p-4 bg-white rounded-[4px] border border-gray-200">
        {/* Filter part */}
        <Form form={form} onValuesChange={handleFormChange}>
          <div className="mb-4">
            <div className="mt-2">
              <Row gutter={[16, 16]}>
                {/* <Col xs={24} sm={24} md={12} lg={8} xl={6}>
                <Input
                  className="input_text_3 mr10 !h-full"
                  name="PurchaseNo"
                  placeholder="No"
                  value={filter?.PurchaseNo}
                  onChange={(e) => {
                    const { value } = e.target;
                    // Allow only positive integers or empty string
                    if (/^[1-9]\d*$/.test(value) || value === "") {
                      handleFilterChange(e);
                    }
                  }}
                />
              </Col> */}
                <Col xs={24} sm={24} md={12} lg={6} xl={6}>
                  <Input
                    className="input_text_3 mr10 !h-full"
                    name="PurchaseNo"
                    placeholder="No"
                    value={filter.PurchaseNo}
                    onChange={(e) => {
                      const { value } = e.target;
                      // Allow only positive integers or empty string
                      if (/^[0-9]\d*$/.test(value) || value === "") {
                        handleFilterChange(e);
                      }
                    }}
                    onKeyDown={(e) => {
                      // Prevent 'e', 'E', '+', '-' keys
                      const blockedKeys = ["e", "E", "+", "-", "."];
                      if (blockedKeys.includes(e.key)) {
                        e.preventDefault();
                      }
                      if (/^[0-9]$/.test(e.key)) {
                        const input = e.target;
                        const { selectionStart, selectionEnd, value } = input;
                        const nextValue =
                          value.slice(0, selectionStart) +
                          e.key +
                          value.slice(selectionEnd);

                        if (!/^(0|[1-9]\d*)$/.test(nextValue)) {
                          e.preventDefault();
                        }
                      }
                    }}
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={8} xl={6}>
                  <CustomDateTimePicker
                    name="PurchaseTime"
                    placeholder="Purchase Time"
                    format="YYYY-MM-DD HH:mm" // 24-hour format
                    value={
                      filter.PurchaseTime ? dayjs(filter.PurchaseTime) : null
                    }
                    onChange={(date) =>
                      setFilter({
                        ...filter,
                        PurchaseTime: date
                          ? date.format("YYYY-MM-DD HH:mm:ss")
                          : "",
                      })
                    }
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={8} xl={6}>
                  <Input
                    className="input_text_3 mr10 !h-full"
                    name="Comment"
                    placeholder="PO Number"
                    value={filter.Comment}
                    onChange={handleFilterChange}
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={8} xl={6}>
                  <Select
                    showSearch
                    placeholder="Purchase Type"
                    optionFilterProp="label"
                    value={
                      typeof filter.PurchaseType === "number"
                        ? Number(filter.PurchaseType)
                        : undefined
                    }
                    onChange={(value) =>
                      setFilter({ ...filter, PurchaseType: value })
                    }
                    options={purchaseTypeOptions}
                    style={{ width: "100%" }}
                  />
                </Col>
                {!isDealerAccount ? (
                  <>
                    {!isOemAccount && (
                      <Col xs={24} sm={24} md={12} lg={8} xl={6}>
                        <Select
                          showSearch
                          placeholder="OEM"
                          optionFilterProp="label"
                          value={
                            filter.OemNo || typeof filter.OemNo === "number"
                              ? Number(filter.OemNo)
                              : null
                          }
                          onChange={(value) =>
                            setFilter({
                              ...filter,
                              OemNo: value,
                              DealerNo: null,
                            })
                          }
                          options={modifiedOems}
                          style={{ width: "100%" }}
                        />
                      </Col>
                    )}
                    <Col xs={24} sm={24} md={12} lg={8} xl={6}>
                      <Select
                        showSearch
                        placeholder="Dealer"
                        optionFilterProp="label"
                        value={
                          filter.DealerNo || typeof filter.DealerNo === "number"
                            ? Number(filter.DealerNo)
                            : null
                        }
                        onChange={(value) =>
                          setFilter({ ...filter, DealerNo: value })
                        }
                        options={modifiedDealers}
                        disabled={!isOemAccount && !filter?.OemNo}
                        style={{ width: "100%" }}
                      />
                    </Col>
                  </>
                ) : null}
              </Row>
            </div>

            <div className="w-full overflow-x-auto">
              <div className="flex justify-between my-4 flex-nowrap min-w-[610px]">
                <div className="flex items-center">
                  <Button
                    type="primary"
                    className="mr-2 w-[104px] h-[30px] font-medium"
                    onClick={handleFilter}
                  >
                    <IoIosCheckmarkCircleOutline className="!text-[14px] font-medium" />
                    Apply
                  </Button>
                  <Button
                    className="reset-button"
                    onClick={() => {
                      form.resetFields();
                      handleReset();
                    }}
                  >
                    <HiArrowPath className="!text-[14px] font-medium" />
                    Reset
                  </Button>
                  {canManagePurchase && (
                    <>
                      <Button
                        type="primary"
                        className="mr-2 -ml-2 min-w-[104px] h-[30px] px-[10px] font-medium"
                        onClick={() => navigate("/purchase/prepurchase")}
                      >
                        <PlusOutlined />
                        Pre-Purchase
                      </Button>
                      <Button
                        type="primary"
                        disabled={selectedRowKeys?.length === 0 ? true : false}
                        className="mr-2 w-[104px] h-[30px] font-medium"
                        onClick={() => {
                          setShowModal(true);
                        }}
                      >
                        <DeleteOutlined />
                        Delete
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
            <ConfirmModal
              showModal={showModal}
              setShowModal={setShowModal}
              onDeleteConfirm={onDeleteConfirm}
              setSelectedRowKeys={setSelectedRowKeys}
            />
            <GlobalTable
              columns={PurchaseColumns(
                hoveredRow,
                setShowModal,
                setSelectedRowKeys,
                setParams,
                params,
                logData?.role_value === 7 || logData?.role_value === 8,
              )}
              dataSource={listData}
              rowSelection={canManagePurchase ? rowSelection : null}
              pagination={paginationConfig}
              // onRow={onRow}
              onRow={(record, rowIndex) => {
                return {
                  onMouseEnter: () => setHoveredRow(rowIndex),
                  onMouseLeave: () => setHoveredRow(null),
                  ...onRow?.(record, rowIndex),
                };
              }}
              loading={loading}
            />
          </div>
        </Form>
      </div>
    </div>
  );
};
